// ScrollyViewer — extrahiert aus app/src/components/editor/Viewer/ScrollyViewer.tsx
// (Plan A Mapping-Audit 2026-05-22).
//
// Abstrahiert app/-Coupling:
//   - `useAuth().isAuthenticated` → optional `isAuthenticated?: boolean` Prop
//   - `EditorEventBus` → optional `editorBridge?: render-prop` Slot
//   - `renderFooter?: ReactNode` Slot (Colophon from @scrolly/viewer)
//   - `SelectedPanel`-Typ inline-kopiert (ScrollyViewerCurrentSlide)
//   - `DEFAULT_MAPPINGS` weggelassen (waren leere Defaults, fallen direkt
//     auf "SofiaSansSemiCondensed" durch)
//
// Trust-Hierarchie: Funktion = app/. Hier 1:1-Logik aus app/, nur
// Coupling raus.

import type { CustomFonts, PostJson } from '@scrolly/post-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef as useReactRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import {
  VisualStoryPlayer,
  useBlockVertScroll,
  type Renderer,
  type VisualStoryEventHandler,
} from '@scrolly/renderer';
import useInitializeViewerState from './hooks/useInitializeViewerState';
import useCustomFontLoader from './hooks/useCustomFontLoader';
import { CompanyLogo, type BrandingLogo } from './CompanyLogo';
import { StartScreen } from './StartScreen';
import { clampSceneIdxToZeroIndexed } from './clampSceneIdx';

/**
 * Layout-Settle-Delay vor initialScrollPercent/-SceneIdx-Restore.
 * VisualStoryStore.start() triggert regenerateSlidePositionsCache via
 * Doppel-setTimeout (~10ms inner + ~10ms outer). 100ms ist konservativer
 * Sicherheitspuffer.
 */
const LAYOUT_SETTLE_DELAY_MS = 100;

/** Throttle-Fenster für onScrollProgress: 1s (leading + trailing). */
const SCROLL_PROGRESS_THROTTLE_MS = 1000;

export const VIEWER_ROOT_CONTAINER = 'viewer-root-container';

const VIEWER_FONT_FALLBACK = 'SofiaSansSemiCondensed';

function getWindowScrollMetrics() {
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const viewportHeight = window.innerHeight || doc.clientHeight || 0;
  const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight);
  return {
    max: scrollHeight - viewportHeight,
    scrollTop,
  };
}

/**
 * Inline-Kopie von app/-SelectedPanel ohne 'TEXT_AND_THUMBNAIL'. Spezielle
 * String-Tokens für Editor-Navigation (Background-Slide → 0, Credits-Slide
 * → scrollToCredits, sonst goToSlide(idx)).
 */
export type ScrollyViewerCurrentSlide =
  | number
  | 'CREDITS_AND_SOURCES'
  | 'BACKGROUND_AND_TITELS'
  | 'SOCIAL_PREVIEW';

export type { BrandingLogo } from './CompanyLogo';

interface Props {
  id: string | number;
  post: PostJson;
  /**
   * Editor-only: render-prop für EditorEventHandler (Live-Updates aus
   * Editor an Renderer durchreichen). app/ wraps mit
   * `<EditorEventHandler renderer={...} />`. public/ lässt undefined.
   */
  editorBridge?: (rendererRef: RefObject<Renderer | null>) => ReactNode;
  /**
   * Editor-only: show full Sentence-Bloom quote in preview (no word-by-word
   * replay while editing). Defaults to true when editorBridge is set.
   */
  editorPreview?: boolean;
  /**
   * Editor-only: while editing Titel & Hintergrund, force-hide the bloom SVG
   * overlay (stale FLENSBURG gallery seed must not bleed onto the cover).
   */
  sentenceBloomCoverPreview?: boolean;
  /**
   * Editor-only: bloom quote panel — scroll to typewriter runway start,
   * not the end of slide-bloom (which shows the full quote instantly).
   */
  sentenceBloomQuoteNavigation?: boolean;
  /**
   * Editor-only: aktive Slide-Auswahl in TemplateEditor (Navigation-Hook).
   * public/-Viewer ignoriert.
   */
  currentSlide?: ScrollyViewerCurrentSlide;
  onViewerReady?: () => void;
  onSlideChange?: (slideIndex: number) => void;
  viewerRootContainerRef: RefObject<HTMLElement | null>;
  heightRef: RefObject<HTMLElement | null>;
  blockScrollElementRef: RefObject<HTMLElement | null>;
  brandingLogo?: BrandingLogo | null;
  /**
   * Optional Renderer-Event-Callback (z.B. storyNavigation für GA).
   * Wird via useInitializeViewerState.options.onEvent durchgereicht.
   */
  onEvent?: VisualStoryEventHandler;
  /**
   * imgproxy base URL für Image-Optimization. Leer-String oder undefined
   * fällt auf raw Asset-URL zurück.
   */
  imgproxyUrl?: string;
  /**
   * Auth-State für VisualStoryPlayer's `showQuality` (Quality-Selector in
   * Editor). Optional — public/ ohne Auth lässt false.
   */
  isAuthenticated?: boolean;
  /**
   * Optional Footer-Slot (z.B. Colophon from @scrolly/viewer).
   * Wenn undefined: kein Footer. Wird unterhalb des VisualStoryPlayer
   * gerendert.
   */
  renderFooter?: ReactNode;
  /**
   * Custom StartScreen / Cover. Render-prop bekommt `onStart` (triggert
   * setStarted+scrollToStartSlide) und `progress` (Renderer-Mount-Progress
   * 0..1). Wenn undefined UND nicht embedded UND nicht isOgMode: Default-
   * StartScreen aus packages/viewer/ rendert. Wenn explicit definiert:
   * Custom-Cover (z.B. public/-CoverSection mit Vorlage-Layout).
   */
  renderCover?: (controls: {
    onStart: () => void;
    progress: number;
    isOgMode: boolean;
    fontFamilies: CustomFonts;
    height: number;
  }) => ReactNode;
  /**
   * Skip Default-CompanyLogo aus packages/. public/-Stack rendert eigenen
   * CompanyLogo via chromeOverlays-Pattern (position:absolute innerhalb
   * Phone-Letterbox). Default false → packages/-CompanyLogo rendert
   * position:fixed wenn brandingLogo gesetzt.
   */
  disableCompanyLogo?: boolean;
  /**
   * Embedded mode — strips all viewer chrome (StartScreen, CompanyLogo,
   * Footer) and scopes the font CSS variables to the viewer's outer
   * `<div>` instead of `document.documentElement`. Used by
   * TemplateHoverPreview.
   */
  embedded?: boolean;
  /**
   * OG-Image render mode. Strips StartScreen-CTA, CompanyLogo, Footer.
   * Auto-triggert onStart(false) damit Renderer für Background-Layers
   * in slide[0] aktiv ist.
   */
  isOgMode?: boolean;
  /**
   * Scroll-Source-Polarität (Plan-2 iOS-Fix). DEFAULT "container". Nur der
   * public/-Immersive-Full-Page-Reader (ViewerChrome) reicht "window" durch.
   * Desktop-Phone, Embedded, app/-Editor bleiben "container".
   */
  scrollSource?: 'container' | 'window';
  /**
   * Plan-1.9 Phase 3.A.CP3 — Resume-Position-Restore (0..1). Bei
   * vorhandenem Wert wird nach Layout-Settle viewerFrame.scrollTop =
   * scrollHeight × p gesetzt. Konflikt-Hierarchie:
   * initialSceneIdxOneIndexed > initialScrollPercent > autoAdvanceOnMount.
   */
  initialScrollPercent?: number;
  /**
   * Plan-1.9 Phase 3.A.CP3 — throttled scroll-progress callback (0..1).
   * Wird ~1× pro Sekunde gefeuert. Consumer (public/-ViewerChrome)
   * persistiert via useResumeState.save.
   */
  onScrollProgress?: (percent: number) => void;
  /**
   * Plan-1.9 Phase 3.B.CP2 — Open-Graph-Anchor 1-indexed Scene-Index aus
   * URL. Wird nach Layout-Settle als store.goToSlide(internalIdx)
   * realisiert. Konflikt: gewinnt über initialScrollPercent +
   * autoAdvanceOnMount.
   */
  initialSceneIdxOneIndexed?: number;
  /**
   * Receives Renderer-Mount-Progress (0..1). Wird sofort gefeuert wenn
   * Renderer hochfährt. Hauptkonsument: page-level ProgressHairline.
   */
  onProgress?: (progress: number) => void;
  /**
   * iOS-Audio-Permission-Anchor (2026-05-28). Wenn true: ScrollyViewer
   * triggert sich selbst onStart(false) sobald Renderer ready, ohne dass
   * ein Cover-Button geklickt werden muss. Use-Case: ViewerChrome
   * (public/-Stack) hat ein eigenes Pre-Mount-Cover ausserhalb von
   * ScrollyViewer und mountet ScrollyViewer erst NACH dem Click. Der
   * ScrollyViewer-Mount läuft also bereits im User-Activation-Context,
   * Video.play() darf laufen — aber das interne ScrollyViewer-Cover
   * würde sonst nochmal angezeigt. autoStart={true} überspringt das.
   *
   * Unterschied zu isOgMode: isOgMode versteckt zusätzlich Cover-CTA +
   * CompanyLogo + Footer; autoStart triggert NUR den onStart-Call.
   */
  autoStart?: boolean;
  /**
   * Live visual viewport height (px) from ViewerChrome. When Safari restores
   * the page from bfcache the shell is re-synced and the renderer remeasures.
   */
  viewportHeight?: number | null;
  /** Bumps on each pageshow so renderer remeasures even when height unchanged. */
  viewportSyncGeneration?: number;
}

const dummy = () => () => {};
// SSR-safe: gleicher Snapshot auf Server und Client First-Render (Hydration-
// Match). Renderer setzt nach Mount echten Wert via visualStoryStore.
const SSR_FALLBACK_HEIGHT = 800;
const dummySnapShot = () => SSR_FALLBACK_HEIGHT;

function ScrollyViewer({
  id,
  post,
  editorBridge,
  editorPreview,
  sentenceBloomCoverPreview,
  sentenceBloomQuoteNavigation,
  currentSlide,
  onViewerReady,
  onSlideChange,
  viewerRootContainerRef,
  heightRef,
  blockScrollElementRef,
  brandingLogo,
  onEvent,
  imgproxyUrl,
  isAuthenticated = false,
  renderFooter,
  renderCover,
  disableCompanyLogo = false,
  embedded = false,
  isOgMode = false,
  scrollSource = 'container',
  initialScrollPercent,
  onScrollProgress,
  initialSceneIdxOneIndexed,
  onProgress,
  autoStart = false,
  viewportHeight = null,
  viewportSyncGeneration = 0,
}: Props) {
  const [realHeight, setRealHeight] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRealHeight(window.innerHeight);
    }
  }, []);

  const [progress, setProgressInternal] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [started, setStarted] = useState(false);

  // Bridge setProgress: internal state + optional consumer callback.
  // Stable callback via ref pattern um inline-arrow-Renders nicht zur
  // useInitializeViewerState-Re-Init zu triggern.
  const onProgressRef = useReactRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  });
  const setProgress = useCallback((p: number | ((prev: number) => number)) => {
    setProgressInternal((prev) => {
      const next = typeof p === 'function' ? p(prev) : p;
      onProgressRef.current?.(next);
      return next;
    });
  }, []);
  const viewerReadyFiredRef = useReactRef(false);
  const fontFamilies = useCustomFontLoader(
    post.data.data.presentationOptions.slideOptions.fontInfos,
    post.data.data.presentationOptions.slideOptions.fontMapping,
  );

  const {
    htmlRendererRef,
    mainContainerRef,
    canvasContainerRef,
    visualStoryStore,
    renderer,
    onResize,
  } = useInitializeViewerState(
    id,
    post,
    setProgress,
    setCurrentSlideIndex,
    setStarted,
    viewerRootContainerRef,
    heightRef,
    fontFamilies,
    { imgproxyUrl, onEvent, scrollSource, editorPreview: editorPreview ?? false, sentenceBloomCoverPreview },
  );

  const { blockScroll, unblockScroll } = useBlockVertScroll({
    resetScroll: true,
    htmlElement: blockScrollElementRef.current,
  });

  useEffect(() => {
    renderer.current?.getJson(post);
  }, [post, renderer]);

  const currentStoryStore = visualStoryStore.current;
  const onStart = useCallback(
    (scrollToStartSlide = true) => {
      if (currentStoryStore && mainContainerRef.current) {
        flushSync(() => {
          setStarted(true);
        });
        currentStoryStore.regenerateSlidePositionsCache();
        if (scrollToStartSlide) {
          // Intro-Tween: scrollt programmatisch bis Slide-1.textCenter (2.2s,
          // quadInOut, cancelable bei User-Input). User sieht die erste
          // Animation einmal automatisch und lernt „aha, das ist scroll-
          // getrieben". Bene-Entscheidung 2026-05-28.
          currentStoryStore.scrollToStartSlide({ intro: true });
        }
      }
    },
    [currentStoryStore, mainContainerRef],
  );

  useEffect(() => {
    if (started) {
      unblockScroll();
      onResize();
    } else {
      blockScroll();
    }
  }, [blockScroll, onResize, started, unblockScroll]);

  // After ViewerChrome re-syncs the shell to visualViewport (pageshow), remeasure
  // the sticky canvas so it fills the viewport — fixes the dead strip at the bottom.
  useEffect(() => {
    if (viewportHeight == null) return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        visualStoryStore.current?.syncFrameDimensions();
        onResize();
      });
    });
    return () => {
      cancelled = true;
    };
  }, [viewportHeight, viewportSyncGeneration, onResize, visualStoryStore]);

  // Auto-start renderer in OG-Mode oder wenn autoStart-Prop gesetzt
  // (iOS-Audio-Permission-Anchor; ViewerChrome mountet ScrollyViewer
  // erst nach Pre-Mount-Cover-Click, der Mount selbst ist also schon
  // im User-Activation-Context — autoStart überspringt das interne
  // Cover, ohne sonstige isOgMode-Effekte zu erzwingen).
  //
  // Unterschied OG vs autoStart (2026-05-28 — Cover-Doppelmount-Fix):
  // - OG-Mode: onStart(false) ohne scroll. Headless-Screenshot soll
  //   slide[0] mit Cover-Hintergrund zeigen.
  // - autoStart: onStart(true) MIT scrollToStartSlide. Beim Stage-
  //   Gating hat User bereits pre-Mount geklickt, Cover ist in
  //   ViewerChrome (außerhalb ScrollyViewer). Ohne Auto-Scroll würde
  //   der spacer-Bereich in VisualStoryPlayer als Cover-shaped
  //   leerer Bereich wirken — der User sähe "noch ein Cover".
  //   scrollToStartSlide() springt zu slide[0].textCenter und
  //   überspringt damit den spacer.
  useEffect(() => {
    if (isOgMode && !started && currentStoryStore && mainContainerRef.current) {
      onStart(false);
      return;
    }
    if (
      autoStart &&
      !started &&
      currentStoryStore &&
      mainContainerRef.current
    ) {
      onStart(true);
    }
  }, [
    isOgMode,
    autoStart,
    started,
    currentStoryStore,
    onStart,
    mainContainerRef,
  ]);

  // Studio scrollys (phased SVG charts, maps, typewriters, 3D scenes)
  // have NO virtual start screen — slides[0] IS the title rendered at
  // scroll=0. Detect by walking the layer tree for any svg-background or
  // object3d layer.
  const isStudioScrolly = useMemo(() => {
    const layers = (
      post as unknown as { data?: { data?: { layers?: unknown[] } } }
    )?.data?.data?.layers as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(layers)) return false;
    const isStudioLayer = (
      layer: Record<string, unknown> | undefined,
    ): boolean => {
      if (!layer) return false;
      if (layer.type === 'svg' && layer.isBackground === true) return true;
      if (layer.type === 'object3d') return true;
      const content = layer.content as { layer_ids?: string[] } | undefined;
      if (content && Array.isArray(content.layer_ids)) {
        for (const childId of content.layer_ids) {
          const child = layers.find(
            (l) => (l as { id?: string }).id === childId,
          );
          if (isStudioLayer(child)) return true;
        }
      }
      return false;
    };
    return layers.some(isStudioLayer);
  }, [post]);

  useEffect(() => {
    if (currentStoryStore && progress >= 1 && currentSlide !== undefined) {
      if (currentSlide === 'BACKGROUND_AND_TITELS') {
        currentStoryStore.viewerFrame.scrollTop = 0;
        currentStoryStore.scrollTop = 0;
        currentStoryStore.post?.slidePlayer.setTimer(0);
      } else if (currentSlide === 'CREDITS_AND_SOURCES') {
        currentStoryStore.scrollToCredits();
      } else if (typeof currentSlide === 'number') {
        const targetSlide = isStudioScrolly ? currentSlide : currentSlide + 1;
        if (sentenceBloomQuoteNavigation) {
          // Bloom presentation slides: [slide-1, slide-bloom (empty spacer), slide-2].
          // The quote text lives on the LAST slide (slide-2). Scrolling to the
          // spacer (index 1) lands in dead space ("scrolls too far / no text").
          currentStoryStore.goToSlide(currentStoryStore.slideCount - 1);
        } else {
          currentStoryStore.goToSlide(targetSlide);
        }
      }
    }
  }, [
    currentSlide,
    currentStoryStore,
    progress,
    isStudioScrolly,
    sentenceBloomQuoteNavigation,
  ]);

  // Sync slide index back to parent for visual highlight.
  useEffect(() => {
    if (onSlideChange && progress >= 1) {
      const reported = isStudioScrolly
        ? currentSlideIndex + 1
        : currentSlideIndex;
      onSlideChange(reported);
    }
  }, [currentSlideIndex, onSlideChange, progress, isStudioScrolly]);

  // Fire once: when progress first reaches 1, call onStart + onViewerReady.
  useEffect(() => {
    if (progress < 1) {
      viewerReadyFiredRef.current = false;
      return;
    }
    if (onViewerReady && !viewerReadyFiredRef.current) {
      viewerReadyFiredRef.current = true;
      queueMicrotask(() => {
        onStart(false);
        onViewerReady();
      });
    }
  }, [onStart, onViewerReady, progress]);

  // Plan-1.9 Phase 3.A.CP3 — initialScrollPercent Apply (Resume-Restore).
  useEffect(() => {
    if (
      typeof initialScrollPercent !== 'number' ||
      initialScrollPercent <= 0 ||
      initialScrollPercent > 1
    ) {
      return;
    }
    if (typeof initialSceneIdxOneIndexed === 'number') {
      // Konflikt: Hash-Anchor gewinnt
      return;
    }
    const timer = window.setTimeout(() => {
      if (scrollSource === 'window') {
        const { max } = getWindowScrollMetrics();
        if (max <= 0) return;
        window.scrollTo(0, max * initialScrollPercent);
        return;
      }
      const el = viewerRootContainerRef.current;
      if (!el) return;
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return;
      el.scrollTop = max * initialScrollPercent;
    }, LAYOUT_SETTLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    initialScrollPercent,
    initialSceneIdxOneIndexed,
    scrollSource,
    viewerRootContainerRef,
  ]);

  // Plan-1.9 Phase 3.B.CP2 — initialSceneIdxOneIndexed Apply.
  useEffect(() => {
    if (typeof initialSceneIdxOneIndexed !== 'number') return;
    const timer = window.setTimeout(() => {
      const store = visualStoryStore.current;
      if (!store) return;
      const internalIdx = clampSceneIdxToZeroIndexed(
        initialSceneIdxOneIndexed,
        store.slideCount,
      );
      if (internalIdx === null) return;
      store.goToSlide(internalIdx);
    }, LAYOUT_SETTLE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [initialSceneIdxOneIndexed, visualStoryStore]);

  // Plan-1.9 Phase 3.A.CP3 — throttled scroll-progress callback.
  // Eigener scroll-listener am viewerRootContainerRef parallel zur
  // VisualStoryStore-internen syncTimerToScroll-RAF.
  useEffect(() => {
    if (!onScrollProgress) return;
    const el = viewerRootContainerRef.current;
    if (scrollSource === 'container' && !el) return;
    let progressFireTimer: number | null = null;
    let progressLastFiredAt = 0;
    const fireProgressNow = () => {
      const metrics =
        scrollSource === 'window'
          ? getWindowScrollMetrics()
          : {
              max: (el?.scrollHeight ?? 0) - (el?.clientHeight ?? 0),
              scrollTop: el?.scrollTop ?? 0,
            };
      const max = metrics.max;
      const percent =
        max > 0 ? Math.max(0, Math.min(1, metrics.scrollTop / max)) : 0;
      onScrollProgress(percent);
      progressLastFiredAt = Date.now();
      progressFireTimer = null;
    };
    const onScrollProgressEvent = () => {
      const elapsed = Date.now() - progressLastFiredAt;
      if (
        elapsed >= SCROLL_PROGRESS_THROTTLE_MS &&
        progressFireTimer === null
      ) {
        fireProgressNow();
      } else if (progressFireTimer === null) {
        progressFireTimer = window.setTimeout(
          fireProgressNow,
          Math.max(0, SCROLL_PROGRESS_THROTTLE_MS - elapsed),
        );
      }
    };
    const scrollTarget = scrollSource === 'window' ? window : el;
    scrollTarget?.addEventListener('scroll', onScrollProgressEvent, {
      passive: true,
    });
    return () => {
      if (progressFireTimer !== null) window.clearTimeout(progressFireTimer);
      scrollTarget?.removeEventListener('scroll', onScrollProgressEvent);
    };
  }, [onScrollProgress, scrollSource, viewerRootContainerRef]);

  const innerHeight = useSyncExternalStore<number>(
    (visualStoryStore.current &&
      visualStoryStore.current.subscribeToHeightChanges) ||
      dummy,
    (visualStoryStore.current && visualStoryStore.current.heightSnapshot) ||
      dummySnapShot,
    (visualStoryStore.current && visualStoryStore.current.heightSnapshot) ||
      dummySnapShot,
  );

  const headingFontFamily =
    fontFamilies.headlineFontFamily || VIEWER_FONT_FALLBACK;
  const bodyFontFamily = fontFamilies.textFontFamily || VIEWER_FONT_FALLBACK;

  useEffect(() => {
    if (embedded) return;
    document.documentElement.style.setProperty(
      '--font-heading',
      `'${headingFontFamily}'`,
    );
    document.documentElement.style.setProperty(
      '--font-body',
      `'${bodyFontFamily}'`,
    );
  }, [embedded, headingFontFamily, bodyFontFamily]);

  const wrapperStyle: CSSProperties = {
    position: 'relative',
    visibility: realHeight === null ? 'hidden' : 'visible',
  };
  if (embedded) {
    (wrapperStyle as Record<string, string>)['--font-heading'] =
      `'${headingFontFamily}'`;
    (wrapperStyle as Record<string, string>)['--font-body'] =
      `'${bodyFontFamily}'`;
  }

  // Cover-Slot: renderCover override-prop (public/) vs Default-StartScreen
  // (app/). embedded=true skipped beides.
  const coverElement = embedded ? null : renderCover !== undefined ? (
    renderCover({
      onStart: () => onStart(),
      progress,
      isOgMode,
      fontFamilies,
      height: innerHeight,
    })
  ) : (
    <StartScreen
      post={post}
      progress={progress}
      onStartClick={onStart}
      fontFamilies={fontFamilies}
      height={innerHeight}
      isOgMode={isOgMode}
    />
  );

  return (
    <div style={wrapperStyle}>
      {editorBridge ? editorBridge(renderer) : null}
      {coverElement}
      {!embedded && !isOgMode && !disableCompanyLogo && (
        <CompanyLogo post={post} brandingLogo={brandingLogo} />
      )}
      <VisualStoryPlayer
        mainContainerRef={mainContainerRef}
        post={post}
        visualStoryStore={currentStoryStore}
        showQuality={isAuthenticated}
        canvasContainerRef={canvasContainerRef}
        htmlContainerRef={htmlRendererRef}
        currentSlideIndex={currentSlideIndex}
        fontFamilies={fontFamilies}
        bodyScroll={scrollSource === 'window'}
      />
      {!embedded && !isOgMode && renderFooter}
    </div>
  );
}

export { ScrollyViewer };
export default ScrollyViewer;
